import { Label } from "layout/typography/Label";
import { InputSwitch } from "primereact/inputswitch";
import { useEffect } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { IPDFContent } from "types/Customize";
import { OrderType } from "types/Order/OrderType";

import { StyledCard } from "../styled";
import { PdfPreview } from "./PdfPreview";
import {
  StyledPreviewColumns,
  StyledSettingRow,
  StyledSettingsCard,
  StyledSettingsDescription,
  StyledSettingsHeader,
  StyledSettingsNote,
  StyledSettingSubtitle,
  StyledSettingTitle,
} from "./preview";

interface IPDFContentPageFields {
  "GFE-noDiscountPdf"?: boolean;
  "GFE-addNoteToPdf"?: boolean;
  "GFE-version3Pdf"?: boolean;
  "Bundled-noDiscountPdf"?: boolean;
  "Bundled-addNoteToPdf"?: boolean;
  "Bundled-version3Pdf"?: boolean;
  "Bariatrics-noDiscountPdf"?: boolean;
  "Bariatrics-addNoteToPdf"?: boolean;
  "Bariatrics-version3Pdf"?: boolean;
  "ED-noDiscountPdf"?: boolean;
  "ED-addNoteToPdf"?: boolean;
  "ED-version3Pdf"?: boolean;
  "PatientResponsibility-noDiscountPdf"?: boolean;
  "PatientResponsibility-addNoteToPdf"?: boolean;
  "PatientResponsibility-version3Pdf"?: boolean;
}

// Version 3 leads: it decides whether the other two are shown at all.
const pdfSettings = [
  {
    field: "version3Pdf",
    title: "PDF version 3",
    subtitle:
      "Switch to the flat fee layout with estimated hours and the payment schedule disclaimer.",
  },
  {
    field: "noDiscountPdf",
    title: "No-Discount PDF",
    subtitle:
      "Drop the prompt pay discount and show a per-provider payment breakdown instead.",
  },
  {
    field: "addNoteToPdf",
    title: "Add Note to PDF",
    subtitle:
      "Ask for a note before downloading and save it to the estimate history.",
  },
];

export function PDFContentForm({
  customFields,
  orderType,
}: {
  customFields?: IPDFContent;
  orderType: OrderType;
}) {
  const { control, setValue, watch } = useFormContext<IPDFContentPageFields>();

  const {
    noDiscountPdf: savedNoDiscountPdf,
    addNoteToPdf: savedAddNoteToPdf,
    version3Pdf: savedVersion3Pdf,
  } = customFields || {};

  // Depends on the saved values, not on the customFields object: an unstable
  // prop would otherwise re-run setValue on every render and loop.
  useEffect(() => {
    setValue(`${orderType}-noDiscountPdf`, savedNoDiscountPdf || false);
    setValue(`${orderType}-addNoteToPdf`, savedAddNoteToPdf || false);
    setValue(`${orderType}-version3Pdf`, savedVersion3Pdf || false);
  }, [
    orderType,
    savedNoDiscountPdf,
    savedAddNoteToPdf,
    savedVersion3Pdf,
    setValue,
  ]);

  const noDiscountPdf = watch(`${orderType}-noDiscountPdf`);
  const addNoteToPdf = watch(`${orderType}-addNoteToPdf`);
  const version3Pdf = watch(`${orderType}-version3Pdf`);

  /**
   * Version 3 replaces the layout the other two act on, so while it is on they
   * change nothing and are hidden. Their values stay in the form — react-hook-
   * form keeps values for unmounted fields — so Save still writes them and they
   * come back untouched when version 3 goes off.
   */
  const visibleSettings = version3Pdf
    ? pdfSettings.filter((setting) => setting.field === "version3Pdf")
    : pdfSettings;

  return (
    <StyledCard
      data-test-id="customize-pdf-content"
      title="Estimate PDF Content"
    >
      <StyledPreviewColumns>
        <StyledSettingsCard>
          <StyledSettingsHeader>
            <i className="pi pi-sliders-h" />
            PDF SETTINGS
          </StyledSettingsHeader>

          <StyledSettingsDescription>
            Turn a setting on to change the PDF staff download from the estimate
            page. The preview beside it updates as you go.
          </StyledSettingsDescription>

          {visibleSettings.map((setting) => (
            <StyledSettingRow key={setting.field}>
              <div>
                <Label
                  htmlFor={`${orderType}-${setting.field}`}
                  data-test-id={`${orderType}-${setting.field}`}
                >
                  <StyledSettingTitle>{setting.title}</StyledSettingTitle>
                </Label>
                <StyledSettingSubtitle>
                  {setting.subtitle}
                </StyledSettingSubtitle>
              </div>

              <Controller
                name={
                  `${orderType}-${setting.field}` as keyof IPDFContentPageFields
                }
                control={control}
                render={({ field: { onChange, value } }) => (
                  <InputSwitch
                    inputId={`${orderType}-${setting.field}`}
                    data-test-id={`${orderType}-${setting.field}-switch`}
                    checked={!!value}
                    onChange={(event) => onChange(event.value)}
                  />
                )}
              />
            </StyledSettingRow>
          ))}

          {version3Pdf && (
            <StyledSettingsNote>
              <i className="pi pi-info-circle" />
              <span>
                No-Discount PDF and Add Note to PDF are hidden because version 3
                overrides both.
              </span>
            </StyledSettingsNote>
          )}
        </StyledSettingsCard>

        <PdfPreview
          noDiscountPdf={noDiscountPdf}
          addNoteToPdf={addNoteToPdf}
          version3Pdf={version3Pdf}
        />
      </StyledPreviewColumns>
    </StyledCard>
  );
}
